/**
 * PATH: packages/email-hub/src/feature/store/aiCopilotStore.ts
 * FILE_ID: EMAILHUB-FEATURE-STORE-AICOPILOT-001
 * PURPOSE: Cross-surface seam that lets ANY email surface (context menu,
 *          reader chips) open the inbox's AI co-pilot tab for a specific
 *          email, optionally seeding the chat input.
 * WHY: Operator 2026-07-09: "Ask AI should open in the side drawer where
 *      AI analysis is present.. and we can chat and get things actioned
 *      there.. just like co-pilot". The AI tab was InboxPage-local
 *      useState — nothing outside the page could flip it. Mirrors the
 *      composeDockStore pattern (module-level zustand, callers use
 *      getState() from non-React contexts).
 * LANDMINE: hostCount gates the context menu's behaviour — when NO host
 *      is mounted (folder pages, kanban reader) the menu falls back to
 *      the RequestRuleDialog modal instead of dispatching into a void.
 */
import { create } from 'zustand'

export interface AiCopilotRequest {
  emailId: string
  /** Optional prefill for the chat input (e.g. "File emails like this…"). */
  seed?: string
  /** Monotonic nonce so re-opening for the same email still triggers. */
  nonce: number
}

interface AiCopilotState {
  request: AiCopilotRequest | null
  hostCount: number
  openFor: (emailId: string, seed?: string) => void
  /** Host acknowledges + clears the pending request. */
  consume: () => void
  /** Host (InboxPage AI tab) registers on mount; returns unregister. */
  registerHost: () => () => void
}

let _nonce = 0

export const useAiCopilotStore = create<AiCopilotState>((set) => ({
  request: null,
  hostCount: 0,
  openFor: (emailId, seed) =>
    set({ request: { emailId, seed, nonce: ++_nonce } }),
  consume: () => set({ request: null }),
  registerHost: () => {
    set((s) => ({ hostCount: s.hostCount + 1 }))
    return () => set((s) => ({ hostCount: Math.max(0, s.hostCount - 1) }))
  },
}))

/** Feature-detection for non-React callers (context menu): is a co-pilot
 *  host currently mounted to receive an openFor()? */
export function hasAiCopilotHost(): boolean {
  return useAiCopilotStore.getState().hostCount > 0
}
